import React from "react";
import { Smartphone, Download, AlertTriangle, CheckCircle2, Info, HardDrive, Tag, Cpu } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { INSTALL_INFO, APP_DOWNLOADS } from "@/lib/downloads";

/**
 * Reusable modal that explains how to sideload an Android APK.
 *
 * Props:
 *   open      {boolean}    — whether the modal is visible
 *   onOpenChange {func}    — setter for open state
 *   app       {object}     — the app being downloaded (for specs display)
 *   onDownload {func}      — optional callback to re-trigger download
 */
export default function InstallInstructions({ open, onOpenChange, app, onDownload }) {
  const config = APP_DOWNLOADS[app?.app_id] || {};
  const specs = INSTALL_INFO.specs;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Smartphone className="w-6 h-6 text-[#7CC576]" />
             Android ထည့်သွင်းနည်း (Sideload)
          </DialogTitle>
          <DialogDescription>
            {app?.title || "App"} ကိုဖုန်းပေါ်သို့ ထည့်သွင်းရန် အဆင့်များ
          </DialogDescription>
        </DialogHeader>

        {/* Specs card */}
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 grid grid-cols-2 gap-3 text-sm">
          <SpecRow icon={<Cpu className="w-4 h-4" />} label="Min Android" value={specs.minAndroid} />
          <SpecRow icon={<Smartphone className="w-4 h-4" />} label="Target" value={specs.targetAndroid} />
          <SpecRow icon={<Tag className="w-4 h-4" />} label="ဗားရှင်း" value={specs.version} />
          <SpecRow icon={<HardDrive className="w-4 h-4" />} label="အရွယ်အစား" value={config.size_mb ? `${config.size_mb} MB` : "—"} />
          <SpecRow icon={<Download className="w-4 h-4" />} label="APK" value={specs.recommendedApk} />
        </div>

        {/* Requirements */}
        <Section title="လိုအပ်ချက်များ — Requirements" icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />}>
          <ul className="space-y-2">
            {INSTALL_INFO.requirements.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="flex-shrink-0">{r.icon}</span>
                <div>
                  <div className="text-zinc-800">{r.text}</div>
                  <div className="text-xs text-zinc-500">{r.text_en}</div>
                </div>
              </li>
            ))}
          </ul>
        </Section>

        {/* Install steps */}
        <Section title="ထည့်သွင်းနည်း — How to install" icon={<Download className="w-5 h-5 text-[#7CC576]" />}>
          <ol className="space-y-2">
            {INSTALL_INFO.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#7CC576] text-white text-xs font-bold grid place-items-center">
                  {i + 1}
                </span>
                <div>
                  <div className="text-zinc-800">{s.text}</div>
                  <div className="text-xs text-zinc-500">{s.text_en}</div>
                </div>
              </li>
            ))}
          </ol>
        </Section>

        {/* Troubleshooting */}
        <Section title="ပြဿနာဖြေရှင်းခြင်း — Troubleshooting" icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}>
          <ul className="space-y-2">
            {INSTALL_INFO.troubleshooting.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm rounded-lg bg-amber-50 border border-amber-200 p-3">
                <span className="flex-shrink-0">{t.icon}</span>
                <div>
                  <div className="text-amber-900">{t.text}</div>
                  <div className="text-xs text-amber-700">{t.text_en}</div>
                </div>
              </li>
            ))}
          </ul>
        </Section>

        {onDownload && (
          <DialogFooter>
            <Button
              onClick={() => onDownload()}
              className="bg-[#7CC576] hover:bg-[#5FB259] rounded-lg"
            >
              <Download className="w-4 h-4 mr-2" /> APK ပြန် Download ဆွဲရန်
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SpecRow({ icon, label, value, mono }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium">
        {icon}
        {label}
      </div>
      <div className={`font-medium text-zinc-900 ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 font-semibold text-zinc-900 mb-2">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}